import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Play, Settings, Trophy, BarChart3, Users, Heart, Gamepad2, Target, BookOpen, Zap } from 'lucide-react';

interface IntroScreenProps {
  onStart: () => void;
}

const IntroScreen: React.FC<IntroScreenProps> = ({ onStart }) => {
  const [showFeatures, setShowFeatures] = useState(false);

  const features = [
    {
      icon: BarChart3,
      title: 'Life Simulation',
      description: 'Experience realistic life decisions and their consequences',
      color: 'from-blue-500 to-cyan-500',
    },
    {
      icon: Heart,
      title: 'Health & Wellness',
      description: 'Track your physical and mental health journey',
      color: 'from-red-500 to-pink-500',
    },
    {
      icon: Users,
      title: 'Relationships',
      description: 'Build and maintain meaningful connections',
      color: 'from-purple-500 to-indigo-500',
    },
    {
      icon: Trophy,
      title: 'Achievements',
      description: 'Unlock achievements and reach life milestones',
      color: 'from-yellow-500 to-orange-500',
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
      <div className="max-w-6xl mx-auto text-center">
        {/* Main Title */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: 'easeOut' }}
          className="mb-12"
        >
          <motion.div
            initial={{ scale: 0.8 }}
            animate={{ scale: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="w-24 h-24 bg-gradient-to-br from-primary-500 to-secondary-500 rounded-3xl flex items-center justify-center mx-auto mb-8 shadow-strong"
          >
            <span className="text-4xl">🏃</span>
          </motion.div>
          
          <h1 className="text-6xl md:text-8xl font-bold text-white mb-6">
            SppefRun <span className="bg-gradient-to-r from-primary-400 to-secondary-400 bg-clip-text text-transparent">Life</span>
          </h1>
          
          <p className="text-xl md:text-2xl text-slate-300 mb-8 max-w-3xl mx-auto leading-relaxed">
            Navigate the complex journey of life through meaningful decisions. 
            Every choice shapes your future - health, wealth, relationships, and happiness.
          </p>
        </motion.div>

        {/* Game Modes Showcase */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12"
        >
          {/* Sandbox Mode */}
          <motion.div
            whileHover={{ scale: 1.05, y: -5 }}
            className="p-6 bg-slate-800/50 rounded-2xl border border-slate-700 hover:border-blue-500/50 transition-all duration-300"
          >
            <div className="flex items-center justify-center w-16 h-16 bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl mb-4 mx-auto">
              <Gamepad2 size={32} className="text-white" />
            </div>
            <h3 className="text-xl font-bold text-white mb-2">Sandbox Mode</h3>
            <p className="text-slate-400 text-sm mb-4">
              Complete freedom to explore different life paths and set your own goals. Perfect for experimentation.
            </p>
            <div className="flex items-center justify-center space-x-2 text-xs text-blue-400">
              <Zap size={16} />
              <span>Free Play</span>
            </div>
          </motion.div>

          {/* Challenge Mode */}
          <motion.div
            whileHover={{ scale: 1.05, y: -5 }}
            className="p-6 bg-slate-800/50 rounded-2xl border border-slate-700 hover:border-orange-500/50 transition-all duration-300"
          >
            <div className="flex items-center justify-center w-16 h-16 bg-gradient-to-r from-orange-500 to-orange-600 rounded-xl mb-4 mx-auto">
              <Target size={32} className="text-white" />
            </div>
            <h3 className="text-xl font-bold text-white mb-2">Challenge Scenarios</h3>
            <p className="text-slate-400 text-sm mb-4">
              Take on specific challenges with defined goals. Test your strategic thinking and decision-making skills.
            </p>
            <div className="flex items-center justify-center space-x-2 text-xs text-orange-400">
              <Target size={16} />
              <span>5 Scenarios</span>
            </div>
          </motion.div>

          {/* Educational Mode */}
          <motion.div
            whileHover={{ scale: 1.05, y: -5 }}
            className="p-6 bg-slate-800/50 rounded-2xl border border-slate-700 hover:border-emerald-500/50 transition-all duration-300"
          >
            <div className="flex items-center justify-center w-16 h-16 bg-gradient-to-r from-emerald-500 to-emerald-600 rounded-xl mb-4 mx-auto">
              <BookOpen size={32} className="text-white" />
            </div>
            <h3 className="text-xl font-bold text-white mb-2">Educational Mode</h3>
            <p className="text-slate-400 text-sm mb-4">
              Learn through guided narrative with lessons, questions, and reflection points. Perfect for workshops.
            </p>
            <div className="flex items-center justify-center space-x-2 text-xs text-emerald-400">
              <BookOpen size={16} />
              <span>3 Modules</span>
            </div>
          </motion.div>
        </motion.div>

        {/* Main Content */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
          className="mb-12"
        >
        </motion.div>

        {/* Features Grid */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12"
        >
          {features.map((feature, index) => {
            const Icon = feature.icon;
            return (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.6 + index * 0.1 }}
                whileHover={{ scale: 1.05, y: -5 }}
                className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20 hover:bg-white/20 transition-all duration-300"
              >
                <div className={`w-16 h-16 bg-gradient-to-br ${feature.color} rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-medium`}>
                  <Icon size={32} className="text-white" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-2">{feature.title}</h3>
                <p className="text-slate-300 text-sm leading-relaxed">{feature.description}</p>
              </motion.div>
            );
          })}
        </motion.div>

        {/* Call to Action */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.0 }}
          className="space-y-6"
        >
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={onStart}
              className="bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white font-bold py-4 px-8 rounded-2xl shadow-strong hover:shadow-xl transition-all duration-300 flex items-center space-x-3 text-lg"
            >
              <Play size={24} />
              <span>Start Your Life Journey</span>
            </motion.button>
            
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setShowFeatures(!showFeatures)}
              className="bg-white/10 hover:bg-white/20 text-white font-semibold py-4 px-8 rounded-2xl border border-white/30 transition-all duration-300 flex items-center space-x-3"
            >
              <Settings size={20} />
              <span>Learn More</span>
            </motion.button>
          </div>

          {/* Additional Info */}
          {showFeatures && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.5 }}
              className="mt-8 bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10"
            >
              <h3 className="text-2xl font-bold text-white mb-4">How It Works</h3>
              <div className="grid md:grid-cols-3 gap-6 text-left">
                <div>
                  <h4 className="text-lg font-semibold text-primary-400 mb-2">1. Make Decisions</h4>
                  <p className="text-slate-300">Face realistic life choices that impact your health, wealth, relationships, and happiness.</p>
                </div>
                <div>
                  <h4 className="text-lg font-semibold text-primary-400 mb-2">2. See Consequences</h4>
                  <p className="text-slate-300">Watch how your decisions ripple through time, affecting your future opportunities and challenges.</p>
                </div>
                <div>
                  <h4 className="text-lg font-semibold text-primary-400 mb-2">3. Build Your Legacy</h4>
                  <p className="text-slate-300">Create a unique life story and unlock achievements as you navigate through different life stages.</p>
                </div>
              </div>
            </motion.div>
          )}
        </motion.div>

        {/* Footer */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 1.2 }}
          className="mt-16 text-slate-400 text-sm"
        >
          <p>© 2025 SppefRun Life. Experience the journey of a lifetime.</p>
        </motion.div>
      </div>
    </div>
  );
};

export default IntroScreen;
